import { SuggestionData } from 'ckeditor5-premium-features'
import { produce } from 'immer'

import { TrackedChange } from 'models/changes'
import { useShallow } from 'zustand/react/shallow'
import { createWithEqualityFn } from 'zustand/traditional'
import { defaultSuggestionCardFilter } from './components/TrackChangesPanel/constants'

// Modify in SuggestionStore.ts
const deepEqualityFnLimitedWithKey = (a: unknown, b: unknown): boolean => {
    // Handle null/undefined cases first
    if (a === null || a === undefined || b === null || b === undefined) {
        return a === b
    }

    // Type guard for objects
    if (typeof a !== 'object' || typeof b !== 'object') {
        return Object.is(a, b)
    }

    // Now TypeScript knows a and b are non-null objects
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length) return false

        // Check if it's an array of objects with 'id' property
        if (a.length > 0 && a[0] && typeof a[0] === 'object' && 'id' in a[0]) {
            return a.every((item, i) => {
                const bItem = b[i]
                if (
                    !item ||
                    !bItem ||
                    typeof bItem !== 'object' ||
                    !('id' in bItem)
                ) {
                    return false
                }
                return (
                    (item as { id: unknown }).id ===
                    (bItem as { id: unknown }).id
                )
            })
        }
    }

    return Object.is(a, b)
}

export interface SuggestionDataMap {
    id: string
    suggestions: SuggestionData[]
}
interface SuggestionStore {
    // editorAnnotationsRef: React.MutableRefObject<null>
    //make key as string and map it to Editor
    suggestionData: Record<string, SuggestionDataMap>
    // Add methods to update suggestion data and remove suggestions.
    trackChangesData: Record<string, TrackedChange>
    //suggestion card filter
    suggestionCardFilter: string
    //should show history suggestions
    isSuggestionsDataUpdated?: boolean
    // set card should loading state
    isLoadingCards?: boolean
    // track if any track change operation is currently processing
    isAnyTrackChangeProcessing: boolean

    /**set and remove track change data */
    setTrackChangesData: (
        trackChange: Record<string, TrackedChange>,
        removeIds?: string[],
        shouldReset?: boolean
    ) => void
    /**set and remove suggestion */
    addOrUpdateSuggestion: (suggestion: SuggestionDataMap) => void
    removeSuggestionData: (id: string) => void
    /**set and remove bulk suggestions*/
    addOrUpdateSuggestions: (
        dataMap: Record<string, SuggestionDataMap>,
        updateMode: 'merge' | 'replace'
    ) => void
    /**remove bulk suggestions*/
    removeBulkSuggestionData: (ids: string[]) => void
    /**remove all suggestions*/
    removeAllSuggestionData: () => void
    /**get Suggestion with id*/
    getSuggestionData: (id: string) => SuggestionDataMap | undefined
    /**get all suggestion data*/
    getAllSuggestionData: () => Record<string, SuggestionDataMap>
    /**set suggestion card filter */
    setSuggestionCardFilter: (filter: string) => void
    /**set should show history suggestions */
    setIsSuggestionsDataUpdated: (shouldShow: boolean) => void
    /**set state to show cards are loading or not */
    setIsLoadingCards: (isLoadingCards: boolean) => void
    /** Reset suggestion scroll state */
    resetSuggestionScroll: boolean
    /** Set reset suggestion scroll state */
    setResetSuggestionScroll: (value: boolean) => void
    /** Set global track change processing state */
    setTrackChangeProcessing: (isAnyTrackChangeProcessing: boolean) => void
}
/**
 * Creates a zustand store for managing workspace elementItemValue state
 * @returns {ElementItemStoreType} A reactive store with workspace management methods
 */
export const useSuggestionsStore = createWithEqualityFn<SuggestionStore>()(
    (set, get) => {
        // internal helper to remove suggestions by ID or clear all
        const removeSuggestionsHelper = (ids?: string[]) =>
            set((state) => {
                if (!ids) {
                    return { suggestionData: {} }
                }

                const updated = { ...state.suggestionData }
                const validIds = ids.filter(
                    (id) => id && typeof id === 'string'
                )
                validIds.forEach((id) => {
                    if (id in updated) {
                        delete updated[id]
                    }
                })
                return { suggestionData: updated }
            })

        return {
            suggestionData: {},
            trackChangesData: {},
            suggestionCardFilter: defaultSuggestionCardFilter,
            isSuggestionsDataUpdated: false,
            isLoadingCards: false,
            isAnyTrackChangeProcessing: false,

            /** Replace or update trackChangesData */
            setTrackChangesData: (
                trackChange: Record<string, TrackedChange>,
                removeIds?: string[],
                shouldReset = false
            ) => {
                set(
                    produce((draft) => {
                        // If shouldReset is true, clear all track changes data
                        if (shouldReset) {
                            draft.trackChangesData = {}
                            return
                        }

                        // Process removeIds if provided
                        if (removeIds?.length) {
                            removeIds.forEach((id) => {
                                delete draft.trackChangesData[id]
                            })
                        }

                        // Helper function to detect changes
                        const isChangeDetected = (
                            existing: TrackedChange | undefined,
                            incoming: TrackedChange
                        ): boolean => {
                            if (!existing) return true
                            if (existing.status !== incoming.status) return true
                            if (existing.changeType !== incoming.changeType)
                                return true
                            if (existing.changedText !== incoming.changedText)
                                return true

                            // For OPTIONGROUP items, do a deep comparison of trackChangeTypeToChangedTexts
                            const existingTexts =
                                existing.trackChangeTypeToChangedTexts ?? []
                            const incomingTexts =
                                incoming.trackChangeTypeToChangedTexts ?? []

                            if (existingTexts.length !== incomingTexts.length)
                                return true

                            // Create a map of existing items by contentItemElementId for comparison
                            const existingMap = new Map<string, any>()
                            existingTexts.forEach((item) => {
                                if (item?.contentItemElementId) {
                                    existingMap.set(
                                        item.contentItemElementId,
                                        item
                                    )
                                }
                            })

                            // Check if all incoming items match the existing ones
                            for (const incomingItem of incomingTexts) {
                                if (!incomingItem?.contentItemElementId)
                                    continue

                                const existingItem = existingMap.get(
                                    incomingItem.contentItemElementId
                                )
                                if (!existingItem) return true // New item

                                // Compare the properties
                                if (
                                    existingItem.trackChangeType !==
                                    incomingItem.trackChangeType
                                )
                                    return true
                                if (
                                    existingItem.changedText !==
                                    incomingItem.changedText
                                )
                                    return true

                                // Remove from map to track which existing items were matched
                                existingMap.delete(
                                    incomingItem.contentItemElementId
                                )
                            }

                            // If there are unmatched existing items, there's a change
                            if (existingMap.size > 0) return true

                            return false
                        }

                        let hasChanges = false
                        for (const [id, change] of Object.entries(
                            trackChange
                        )) {
                            // Check if this is actually a new or changed item
                            const existingChange = draft.trackChangesData[id]
                            if (isChangeDetected(existingChange, change)) {
                                hasChanges = true
                            }
                            draft.trackChangesData[id] = change
                        }

                        // If there are actual changes, trigger the suggestion update process
                        if (hasChanges || removeIds?.length) {
                            draft.isLoadingCards = true
                            draft.isSuggestionsDataUpdated = false
                        }
                    })
                )
            },

            /** Add or update a single suggestion */
            addOrUpdateSuggestion: (suggestion: SuggestionDataMap) =>
                set(
                    produce((draft) => {
                        draft.suggestionData[suggestion.id] = suggestion
                    })
                ),

            /** Remove a single suggestion by ID */
            removeSuggestionData: (id: string) => removeSuggestionsHelper([id]),

            /** Add or update suggestions, optionally replacing existing ones */
            addOrUpdateSuggestions: (
                dataMap: Record<string, SuggestionDataMap>,
                updateMode: 'merge' | 'replace' = 'merge'
            ) =>
                set(
                    produce<SuggestionStore>((draft) => {
                        if (updateMode === 'replace') {
                            const preserveSet = new Set(Object.keys(dataMap))
                            const existingKeys = Object.keys(
                                draft.suggestionData
                            )

                            existingKeys.forEach((key) => {
                                if (!preserveSet.has(key)) {
                                    delete draft.suggestionData[key]
                                }
                            })
                        }

                        // Skip update if there are no real changes
                        const hasChanges = Object.keys(dataMap).some(
                            (key) =>
                                !draft.suggestionData[key] ||
                                !deepEqualityFnLimitedWithKey(
                                    draft.suggestionData[key],
                                    dataMap[key]
                                )
                        )

                        if (hasChanges) {
                            Object.assign(draft.suggestionData, dataMap)
                        }

                        // 🔍 Optional: debug log for development
                        if (process.env.NODE_ENV === 'development') {
                            console.debug(
                                'Updated suggestions:',
                                Object.keys(dataMap).length
                            )
                        }
                    })
                ),

            /** Remove multiple suggestions by IDs */
            removeBulkSuggestionData: (ids: string[]) =>
                removeSuggestionsHelper(ids),

            /** Clear all suggestions */
            removeAllSuggestionData: () => removeSuggestionsHelper(),

            /** Selector to get one suggestion */
            getSuggestionData: (id: string) => get().suggestionData[id],

            /** Selector to get all suggestions */
            getAllSuggestionData: () => get().suggestionData,

            /** Set the active filter */
            setSuggestionCardFilter: (filter: string) =>
                set(() => ({ suggestionCardFilter: filter })),

            /** Update the suggestions "dirty" state */
            setIsSuggestionsDataUpdated: (shouldShow: boolean) =>
                set(() => ({ isSuggestionsDataUpdated: shouldShow })),

            /** Toggle loading state */
            setIsLoadingCards: (isLoadingCards: boolean) =>
                set(() => ({ isLoadingCards })),
            /** Reset suggestion scroll state */
            resetSuggestionScroll: false,
            /** Set reset suggestion scroll state */
            setResetSuggestionScroll: (value: boolean) =>
                set({ resetSuggestionScroll: value }),

            /** Set global track change processing state */
            setTrackChangeProcessing: (isAnyTrackChangeProcessing) =>
                set({ isAnyTrackChangeProcessing }),
        }
    }
)

/**
 * A custom hook that retrieves a shallowly selected value from the `useSuggestionsStore` store.
 *
 * Sample usage:
 * ```typescript
 * const { suggestionData } = useSuggestionsShallowSelector((state) => ({
 *   suggestionData: state.suggestionData,
 * }));
 * ```
 *
 * @param selector - A function that takes the store state and returns the value to be selected.
 * @returns The selected value from the store.
 */
export const useSuggestionsShallowSelector = <T>(
    selector: (state: SuggestionStore) => T
): T => useSuggestionsStore(useShallow(selector))

/**
 * Selectors to retrieve specific parts of the suggestion store state.
 */
export const selectSuggestionById = (id: string) => (state: SuggestionStore) =>
    state.suggestionData[id]

/** Select all suggestions from the store */
export const selectAllSuggestions = (state: SuggestionStore) =>
    state.suggestionData
